import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiClient } from '../../apiClient';
import { writeAuditLog } from '../../lib/auditLogger';

const UserDetail = () => {
    const { uid } = useParams();
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [processing, setProcessing] = useState(false);
    
    // For account disabling
    const [showDisableModal, setShowDisableModal] = useState(false);
    const [disableReason, setDisableReason] = useState('不正利用');
    const [disableNote, setDisableNote] = useState('');

    const fetchUser = async () => {
        try {
            const response = await apiClient.get(`/v1/admin/users/${uid}`);
            const data = await response.json();
            if (response.ok) {
                setUser(data);
                // Record Audit Log: USER_VIEW (no personal info saved)
                writeAuditLog({
                    action: 'USER_VIEW',
                    resourceType: 'user',
                    resourceId: uid,
                    metadata: { source: 'admin_console' }
                });
            } else {
                setError(data.error || "ユーザー情報の取得に失敗しました。");
            }
        } catch (err) {
            console.error("Error:", err);
            setError("通信エラーが発生しました。");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUser();
    }, [uid]);

    const handleLogout = async () => {
        if (!window.confirm("このユーザーを強制ログアウトしますか？")) return;
        setProcessing(true);
        try {
            const response = await apiClient.post(`/v1/admin/users/${uid}/logout`);
            if (response.ok) {
                alert("強制ログアウトを実行しました。");
            } else {
                const data = await response.json();
                alert(`エラー: ${data.error}`);
            }
        } catch (err) {
            alert("通信エラーが発生しました。");
        } finally {
            setProcessing(false);
        }
    };

    const handleDisable = async () => {
        setProcessing(true);
        try {
            const response = await apiClient.post(`/v1/admin/users/${uid}/disable`, {
                reason: disableReason,
                note: disableNote
            });
            if (response.ok) {
                alert("アカウントを無効化しました。");
                setShowDisableModal(false);
                fetchUser();
            } else {
                const data = await response.json();
                alert(`エラー: ${data.error}`);
            }
        } catch (err) {
            alert("通信エラーが発生しました。");
        } finally {
            setProcessing(false);
        }
    };

    const handleEnable = async () => {
        if (!window.confirm("このアカウントを有効化しますか？")) return;
        setProcessing(true);
        try {
            const response = await apiClient.post(`/v1/admin/users/${uid}/enable`);
            if (response.ok) {
                alert("アカウントを有効化しました。");
                fetchUser();
            } else {
                const data = await response.json();
                alert(`エラー: ${data.error}`);
            }
        } catch (err) {
            alert("通信エラーが発生しました。");
        } finally {
            setProcessing(false);
        }
    };

    if (loading) return <div className="p-8">読み込み中...</div>;
    if (error) return <div className="p-8 text-red-600">{error}</div>;
    if (!user) return <div className="p-8">ユーザーが見つかりません。</div>;

    const isAdmin = user.role === 'admin' || user.role === 'owner';

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">ユーザー詳細</h2>
                <button
                    onClick={() => navigate('/admin/users')}
                    className="text-gray-600 hover:text-gray-900"
                >
                    &larr; 戻る
                </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* 基本情報 */}
                <div className="lg:col-span-2 space-y-6">
                    <div className="bg-white shadow rounded-lg p-6">
                        <h3 className="text-lg font-medium border-b pb-2 mb-4 text-gray-800">基本情報</h3>
                        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <dt className="text-sm font-medium text-gray-500">BillWaveお客様ID</dt>
                                <dd className="mt-1 text-sm text-gray-900">{user.billwave_customer_id}</dd>
                            </div>
                            <div>
                                <dt className="text-sm font-medium text-gray-500">ユーザーID (UID)</dt>
                                <dd className="mt-1 text-sm text-gray-900">{user.uid}</dd>
                            </div>
                            <div>
                                <dt className="text-sm font-medium text-gray-500">企業名</dt>
                                <dd className="mt-1 text-sm text-gray-900">{user.company_name}</dd>
                            </div>
                            <div>
                                <dt className="text-sm font-medium text-gray-500">部署名</dt>
                                <dd className="mt-1 text-sm text-gray-900">{user.department || '-'}</dd>
                            </div>
                            <div>
                                <dt className="text-sm font-medium text-gray-500">氏名</dt>
                                <dd className="mt-1 text-sm text-gray-900">{user.name}</dd>
                            </div>
                            <div>
                                <dt className="text-sm font-medium text-gray-500">メールアドレス</dt>
                                <dd className="mt-1 text-sm text-gray-900">{user.email}</dd>
                            </div>
                            <div>
                                <dt className="text-sm font-medium text-gray-500">電話番号</dt>
                                <dd className="mt-1 text-sm text-gray-900">{user.phoneNumber || '-'}</dd>
                            </div>
                        </dl>
                    </div>

                    {/* 権限情報 */}
                    <div className="bg-white shadow rounded-lg p-6">
                        <h3 className="text-lg font-medium border-b pb-2 mb-4 text-gray-800">権限情報</h3>
                        <div className="mb-4">
                            <span className={`px-3 py-1 rounded-full text-xs font-bold ${isAdmin ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}`}>
                                ロール: {isAdmin ? '管理者' : 'メンバー'}
                            </span>
                        </div>
                        <h4 className="text-sm font-bold text-gray-700 mb-2">機能権限</h4>
                        <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1 text-sm text-gray-600">
                            <li>• 請求書のアップロード: {user.permissions?.invoice?.canUpload || isAdmin ? '✅' : '❌'}</li>
                            <li>• 予約済み請求書の閲覧: {user.permissions?.invoice?.canViewScheduled || isAdmin ? '✅' : '❌'}</li>
                            <li>• 予約済み請求書の削除: {user.permissions?.invoice?.canDeleteScheduled || isAdmin ? '✅' : '❌'}</li>
                            <li>• 請求履歴の閲覧: {user.permissions?.invoice?.canViewHistory || isAdmin ? '✅' : '❌'}</li>
                            <li>• 顧客データのアップロード: {user.permissions?.customer?.canUpload || isAdmin ? '✅' : '❌'}</li>
                            <li>• 顧客データの削除: {user.permissions?.customer?.canDelete || isAdmin ? '✅' : '❌'}</li>
                            <li>• 契約内容の閲覧: {user.permissions?.settings?.canViewSubscription || isAdmin ? '✅' : '❌'}</li>
                            <li>• 契約プランの変更: {user.permissions?.settings?.canChangePlan || isAdmin ? '✅' : '❌'}</li>
                            <li>• BillWave設定の閲覧: {user.permissions?.settings?.canViewBillwave || isAdmin ? '✅' : '❌'}</li>
                            <li>• BillWave設定の更新: {user.permissions?.settings?.canUpdateBillwave || isAdmin ? '✅' : '❌'}</li>
                            <li>• お支払い方法の閲覧: {isAdmin ? '✅' : '❌'}</li>
                            <li>• お支払い方法の更新: {isAdmin ? '✅' : '❌'}</li>
                            <li>• ご利用料金の閲覧: {isAdmin ? '✅' : '❌'}</li>
                        </ul>
                        <p className="mt-4 text-xs text-gray-500">※管理者はすべての権限を標準で保持します。</p>
                    </div>

                    {/* ログイン情報 */}
                    <div className="bg-white shadow rounded-lg p-6">
                        <h3 className="text-lg font-medium border-b pb-2 mb-4 text-gray-800">ログイン情報</h3>
                        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <dt className="text-sm font-medium text-gray-500">最終ログイン日時</dt>
                                <dd className="mt-1 text-sm text-gray-900">
                                    {user.lastSignInTime ? new Date(user.lastSignInTime).toLocaleString() : 'なし'}
                                </dd>
                            </div>
                            <div>
                                <dt className="text-sm font-medium text-gray-500">アカウント作成日時</dt>
                                <dd className="mt-1 text-sm text-gray-900">
                                    {user.creationTime ? new Date(user.creationTime).toLocaleString() : '-'}
                                </dd>
                            </div>
                        </dl>
                    </div>
                </div>

                {/* 操作パネル */}
                <div className="space-y-6">
                    <div className="bg-white shadow rounded-lg p-6">
                        <h3 className="text-lg font-medium border-b pb-2 mb-4 text-red-600">管理操作</h3>
                        <div className="space-y-4">
                            <button
                                onClick={handleLogout}
                                disabled={processing}
                                className="w-full bg-orange-500 text-white py-2 px-4 rounded hover:bg-orange-600 font-bold transition disabled:opacity-50"
                            >
                                強制ログアウト
                            </button>

                            {user.disabled ? (
                                <button
                                    onClick={handleEnable}
                                    disabled={processing}
                                    className="w-full bg-green-600 text-white py-2 px-4 rounded hover:bg-green-700 font-bold transition disabled:opacity-50"
                                >
                                    アカウントを有効化する
                                </button>
                            ) : (
                                <button
                                    onClick={() => setShowDisableModal(true)}
                                    disabled={processing}
                                    className="w-full bg-red-600 text-white py-2 px-4 rounded hover:bg-red-700 font-bold transition disabled:opacity-50"
                                >
                                    アカウントを無効化する
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* 無効化モーダル */}
            {showDisableModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                        <h3 className="text-xl font-bold mb-4">アカウント無効化</h3>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">無効化理由</label>
                                <select
                                    value={disableReason}
                                    onChange={(e) => setDisableReason(e.target.value)}
                                    className="w-full border-gray-300 rounded-md shadow-sm"
                                >
                                    <option value="不正利用">不正利用</option>
                                    <option value="退職者">退職者</option>
                                    <option value="顧客依頼">顧客依頼</option>
                                    <option value="誤登録">誤登録</option>
                                    <option value="その他">その他</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">詳細（メモ）</label>
                                <textarea
                                    value={disableNote}
                                    onChange={(e) => setDisableNote(e.target.value)}
                                    className="w-full border-gray-300 rounded-md shadow-sm h-24"
                                    placeholder="理由の詳細を入力してください"
                                />
                            </div>
                        </div>
                        <div className="mt-6 flex justify-end gap-3">
                            <button
                                onClick={() => setShowDisableModal(false)}
                                className="px-4 py-2 text-gray-600 hover:text-gray-800"
                            >
                                キャンセル
                            </button>
                            <button
                                onClick={handleDisable}
                                disabled={processing}
                                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 font-bold disabled:opacity-50"
                            >
                                無効化を実行
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserDetail;
